/**
 * User Service Layer
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles user profile-related API calls
 * - Dependency Inversion: Depends on axios abstraction
 * - Open/Closed: Easy to add new user methods without modifying existing ones
 * - Interface Segregation: Clean interface for user operations
 * 
 * KISS Principle: Each method does one thing clearly
 * 
 * Components should never call axios directly - they use this service.
 */

import axiosInstance from './axiosInstance';
import { IUser, IUpdateUserProfileDto } from '../../types/auth.types';
import { IUpdateCheckoutProfileDto } from '../../types/checkout-profile.types';
import { AxiosResponse } from 'axios';
import { pickWritableSellerProfile } from '../../utils/sellerProfile';

export type { IUpdateUserProfileDto };

/**
 * User Service Interface (for testing and extensibility)
 */
export interface IUserService {
  getUserProfile(): Promise<IUser>;
  updateUserProfile(data: IUpdateUserProfileDto): Promise<IUser>;
  updateCheckoutProfile(data: IUpdateCheckoutProfileDto): Promise<IUser>;
  changePassword(oldPassword: string, newPassword: string): Promise<void>;
  unlinkGoogleAccount(): Promise<{ message: string }>;
}

/**
 * User Service Implementation
 */
class UserService implements IUserService {
  private readonly baseUrl = '/users';

  /**
   * Get current user profile
   * GET /users/me
   */
  async getUserProfile(): Promise<IUser> {
    const response: AxiosResponse<IUser> = await axiosInstance.get(
      `${this.baseUrl}/me`
    );
    return response.data;
  }

  /**
   * Update user profile
   * PUT /users/me
   * 
   * @param data - Profile update data (firstName, lastName, username)
   * @returns Updated user object
   */
  async updateUserProfile(data: IUpdateUserProfileDto): Promise<IUser> {
    const payload: IUpdateUserProfileDto = {
      ...data,
      sellerProfile: data.sellerProfile
        ? pickWritableSellerProfile(data.sellerProfile)
        : undefined,
    };
    const response: AxiosResponse<{ message: string; user: IUser }> = await axiosInstance.put(
      `${this.baseUrl}/me`,
      payload
    );
    return response.data.user;
  }

  /**
   * Save delivery details for faster checkout
   * PUT /users/me/checkout-profile
   */
  async updateCheckoutProfile(data: IUpdateCheckoutProfileDto): Promise<IUser> {
    const response: AxiosResponse<{ message: string; user: IUser }> =
      await axiosInstance.put(`${this.baseUrl}/me/checkout-profile`, data);
    return response.data.user;
  }

  /**
   * Change user password
   * PUT /users/me/password
   * 
   * @param oldPassword - Current password
   * @param newPassword - New password
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await axiosInstance.put(`${this.baseUrl}/me/password`, {
      oldPassword,
      newPassword,
    });
  }

  /**
   * Disconnect Google sign-in from the current account.
   * DELETE /users/me/accounts/google
   */
  async unlinkGoogleAccount(): Promise<{ message: string }> {
    const response = await axiosInstance.delete(
      `${this.baseUrl}/me/accounts/google`,
    );
    return response.data;
  }
}

// Export singleton instance (KISS - simple to use)
export const userService = new UserService();

// Also export class for testing/mocking
export default UserService;

